// src/page/EventNew.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

export default function EventNew() {
  useAuthGuard();
  const navigate = useNavigate();

  const MSG = {
    apiError: 'サーバーエラーが発生しました',
    errEventName: 'キャンペーン名は必須です',
    errEventRange: '開催期間は必須です（開始・終了を選択してください）',
    errPublishedFlag: '公開設定を選択してください',
    errPublishedRange: '公開期間は必須です（開始・終了を選択してください）',
    errCampaignPoint: 'キャンペーンポイントは必須です（数値）',
    errOrder: '表示順は整数で入力してください',
    saved: '登録しました',
    uploadError: '画像のアップロードに失敗しました。'
  };

  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const fmtRange = (a, b) => (a && b ? `${fmtDate(a)} ~ ${fmtDate(b)}` : '');
  const isIntegerLike = (v) => v !== '' && /^-?\d+$/.test(String(v).trim());

  // form states (empty default)
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [nearest, setNearest] = useState('');
  const [openDisplay, setOpenDisplay] = useState('');
  const [eventOpenDate, setEventOpenDate] = useState('');
  const [eventCloseDate, setEventCloseDate] = useState('');
  const [eventPeriodDisplay, setEventPeriodDisplay] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpenMode, setLinkOpenMode] = useState('1');

  const [campaignPoint, setCampaignPoint] = useState('');
  const [campaignButtonOn, setCampaignButtonOn] = useState(false);
  const [displayOrder, setDisplayOrder] = useState('');
  const [publishedFlag, setPublishedFlag] = useState(null);
  const [publishedStart, setPublishedStart] = useState('');
  const [publishedEnd, setPublishedEnd] = useState('');

  // image (single)
  const [imageFile, setImageFile] = useState(null);      // File | null
  const [imagePreview, setImagePreview] = useState('');  // blob URL
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // --------- spot/ticket (related) ----------
  const [spotId, setSpotId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const spotOptions = useMemo(
    () => spots.map(s => ({ value: s.spotId, label: s.spotName })),
    [spots]
  );
  const [ticketId, setTicketId] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const ticketOptions = useMemo(
    () => tickets.map(t => ({ value: t.packageId, label: t.packageName })),
    [tickets]
  );
  // pairs: [{ spotId, spotName, ticketId, ticketName }]
  const [pairs, setPairs] = useState([]);
  // --------- spot/coupon (related; independent) ----------
  const [spotIdCoupon, setSpotIdCoupon] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const couponOptions = useMemo(
    () => coupons.map(c => ({ value: c.packageId, label: c.packageName })),
    [coupons]
  );
  const [couponId, setCouponId] = useState('');
  const [couponPairs, setCouponPairs] = useState([]); // [{ spotId, spotName, couponId, couponName }]

  const handleAddPair = () => {
    if (!spotId || !ticketId) return;
    const spotName = spotOptions.find(o => o.value === spotId)?.label || '';
    const ticketName = ticketOptions.find(o => o.value === ticketId)?.label || '';
    const dup = pairs.some(p => p.spotId === spotId && p.ticketId === ticketId);
    if (dup) return;
    setPairs(prev => [...prev, { spotId, spotName, ticketId, ticketName }]);
    setTicketId('');
  };
  const handleRemovePair = (idx) => setPairs(prev => prev.filter((_, i) => i !== idx));
  // coupon pair handlers
  const handleAddCouponPair = () => {
    if (!spotIdCoupon || !couponId) return;
    const spotName = spotOptions.find(o => o.value === spotIdCoupon)?.label || '';
    const couponName = couponOptions.find(o => o.value === couponId)?.label || '';
    const dup = couponPairs.some(p => p.spotId === spotIdCoupon && p.couponId === couponId);
    if (dup) return;
    setCouponPairs(prev => [...prev, { spotId: spotIdCoupon, spotName, couponId, couponName }]);
    setCouponId('');
  };
  const handleRemoveCouponPair = (idx) =>
    setCouponPairs(prev => prev.filter((_, i) => i !== idx));

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // flatpickr
  const openRangeRef = useRef(null);
  const publishRangeRef = useRef(null);

  const clearPublish = () => {
    setPublishedStart('');
    setPublishedEnd('');
    try { publishRangeRef.current?.clear(); } catch { /* noop */ }
  };

  useEffect(() => {
    if (!window.flatpickr) return;
    const ja = window.flatpickr.l10ns?.ja || {};

    const openInput = document.getElementById('public_range_main');
    if (openInput) {
      openRangeRef.current = window.flatpickr(openInput, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (ds) => {
          if (ds?.length === 2) {
            setEventOpenDate(ds[0].toISOString());
            setEventCloseDate(ds[1].toISOString());
          } else if (!ds?.length) {
            setEventOpenDate('');
            setEventCloseDate('');
          }
        }
      });
    }

    const pubInput = document.getElementById('public_range_side');
    if (pubInput) {
      publishRangeRef.current = window.flatpickr(pubInput, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (ds) => {
          if (ds?.length === 2) {
            setPublishedStart(ds[0].toISOString());
            setPublishedEnd(ds[1].toISOString());
          } else if (!ds?.length) {
            setPublishedStart('');
            setPublishedEnd('');
          }
        }
      });
    }

    return () => {
      try { openRangeRef.current?.destroy(); } catch { }
      try { publishRangeRef.current?.destroy(); } catch { }
    };
  }, []);

  const invalid = (k) => (errors[k] ? 'is-invalid' : '');
  const feedback = (k) => errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null;

  const validate = () => {
    const err = {};
    if (!eventName.trim()) err.eventName = MSG.errEventName;
    if (!eventOpenDate || !eventCloseDate) err.eventRange = MSG.errEventRange;
    if (publishedFlag !== true && publishedFlag !== false) err.publishedFlag = MSG.errPublishedFlag;
    if (!publishedStart || !publishedEnd) err.publishedRange = MSG.errPublishedRange;
    if (campaignPoint === '' || Number.isNaN(Number(campaignPoint))) err.campaignPoint = MSG.errCampaignPoint;
    if (displayOrder !== '' && !isIntegerLike(displayOrder)) err.displayOrder = MSG.errOrder;
    setErrors(err);
    return err;
  };

  // image helpers
  const fileInputRef = useRef(null);
  const isBlobUrl = (u) => { try { return new URL(u).protocol === 'blob:'; } catch { return false; } };

  const setSingleImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handlePickClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSingleImage(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSingleImage(file);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const clearImage = () => {
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
  };

  useEffect(() => {
    return () => { if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  // fetch spot list for selector
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingSpots(true);
        const token = localStorage.getItem('access_token') || '';
        const res = await fetch(`${API_BASE_URL}/v1/spot?s=0%2C1&page=1&size=200`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(s => ({
          spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
          spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
        })).filter(x => x.spotId && x.spotName);
        setSpots(mapped);
      } catch {
        setSpots([]);
      } finally {
        setLoadingSpots(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // fetch ticket packages for selected spot
  useEffect(() => {
    if (!spotId) { setTickets([]); setTicketId(''); return; }
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingTickets(true);
        const token = localStorage.getItem('access_token') || '';
        const url = new URL(`${API_BASE_URL}/v1/ticket`);
        url.searchParams.set('s', '0,1');
        url.searchParams.set('size', '200');
        url.searchParams.set('sort', 'title');
        url.searchParams.set('dir', 'asc');
        url.searchParams.set('spotid', spotId);
        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        setTickets(Array.isArray(data.items) ? data.items : []);
      } catch {
        setTickets([]);
      } finally {
        setLoadingTickets(false);
      }
    })();
    return () => ac.abort();
  }, [spotId]);

  // fetch coupon packages for selected spot (independent selector)
  useEffect(() => {
    if (!spotIdCoupon) { setCoupons([]); setCouponId(''); return; }
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingCoupons(true);
        const token = localStorage.getItem('access_token') || '';
        const url = new URL(`${API_BASE_URL}/v1/coupon`);
        url.searchParams.set('size', '200');
        url.searchParams.set('sort', 'title');
        url.searchParams.set('dir', 'asc');
        // align with ticket tab behavior (pre+active); adjust if unnecessary
        url.searchParams.set('s', '0,1');
        url.searchParams.set('spotid', spotIdCoupon);
        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        setCoupons(Array.isArray(data.items) ? data.items : []);
      } catch {
        setCoupons([]);
      } finally {
        setLoadingCoupons(false);
      }
    })();
    return () => ac.abort();
  }, [spotIdCoupon]);

  // upload helper
  const uploadImageForEvent = async (eventId) => {
    if (!imageFile) return null;
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token') || '';
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name);
      fd.append('path', `event/${eventId}/`);
      const res = await fetch(`${API_BASE_URL}/v1/file`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: fd
      });
      if (!res.ok) {
        let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      let resultPath = null;
      try { const j = await res.json(); resultPath = j?.path || j?.result || null; }
      catch { resultPath = await res.text(); }
      return resultPath;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    const keys = Object.keys(err);
    if (keys.length) {
      const first = keys[0];
      const node = document.querySelector(`[data-error-key="${first}"]`) || document.querySelector(`[name="${first}"]`);
      if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const token = localStorage.getItem('access_token') || '';
    const dto = {
      event: {
        eventName,
        // eventImage will be set after upload
        eventOpenDate,
        eventCloseDate,
        displayOrder: displayOrder === '' ? undefined : Number(displayOrder),
        publishedFlag,
        publishedStartDate: publishedStart,
        publishedEndDate: publishedEnd,
        eventDescription,
        nearest,
        eventPeriod: eventPeriodDisplay,
        eventLink: linkUrl,
        launcherMode: linkOpenMode,
        eventOpenDisplay: openDisplay,
        campaignPoint: Number(campaignPoint),
        campaignApplicationFlg: !!campaignButtonOn,
      },
      // include selected relations when creating
      related: {
        // tickets (type=1)
        spots: [...new Set(pairs.map(p => p.spotId).filter(Boolean))],
        packages: [...new Set(pairs.map(p => p.ticketId).filter(Boolean))],
        // coupons (type=3)
        couponSpots: [...new Set(couponPairs.map(p => p.spotId).filter(Boolean))],
        coupons: [...new Set(couponPairs.map(p => p.couponId).filter(Boolean))],
      }
    };

    try {
      setSubmitting(true);
      setApiError('');

      // 1) Create event
      const res = await fetch(`${API_BASE_URL}/v1/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(dto)
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const j = await res.json().catch(() => ({}));
      const createdId = j?.event?.eventId || j?.eventId || j?.id;
      if (!createdId) {
        alert(MSG.saved);
        navigate('/event');
        return;
      }

      // 2) Upload image if selected, then update eventImage
      if (imageFile) {
        const uploadedPath = await uploadImageForEvent(createdId);
        if (!uploadedPath) throw new Error(MSG.uploadError);

        const putBody = { event: { eventImage: uploadedPath } };
        const putRes = await fetch(`${API_BASE_URL}/v1/event/${createdId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(putBody)
        });
        if (!putRes.ok) {
          const txt = await putRes.text().catch(() => '');
          throw new Error(txt || `HTTP ${putRes.status}`);
        }
      }

      alert(MSG.saved);
      navigate('/event');
    } catch (e2) {
      console.error(e2);
      setApiError(MSG.apiError);
      alert(MSG.apiError);
    } finally {
      setSubmitting(false);
    }
  };

  const savingOrUploading = submitting || uploading;

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/event">キャンペーン管理</a></li>
            <li className="breadcrumb-item active">新規作成</li>
          </ol>
        </nav>

        {apiError && <div className="alert alert-danger">{apiError}</div>}

        <form className="mb-9" onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">キャンペーン新規作成</h2>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                type="button"
                onClick={() => window.history.back()}
                disabled={savingOrUploading}
              >
                キャンセル
              </button>
              <button
                className="btn btn-primary mb-2 mb-sm-0"
                type="submit"
                disabled={savingOrUploading}
              >
                {uploading ? '画像アップロード中...' : (submitting ? '登録中…' : '登録')}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">
              <div className={`mb-4 ${invalid('eventName')}`} data-error-key="eventName">
                <h4 className="mb-3">キャンペーン名<span className="text-danger">*</span></h4>
                <input
                  className={`form-control ${invalid('eventName')}`}
                  type="text"
                  name="eventName"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  disabled={savingOrUploading}
                />
                {feedback('eventName')}
              </div>

              <div className="mb-4">
                <h4 className="mb-3">キャンペーン概要</h4>
                <textarea
                  className="form-control"
                  rows={4}
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  disabled={savingOrUploading}
                />
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">最寄り</h4>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={nearest}
                      onChange={(e) => setNearest(e.target.value)}
                      disabled={savingOrUploading}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">開催時間</h4>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={openDisplay}
                      onChange={(e) => setOpenDisplay(e.target.value)}
                      disabled={savingOrUploading}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6" data-error-key="eventRange">
                  <div className="mb-4">
                    <h4 className="mb-3">開催期間<span className="text-danger">*</span></h4>
                    <input
                      className={`form-control ${invalid('eventRange')}`}
                      type="text"
                      id="public_range_main"
                      placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                      defaultValue={(eventOpenDate && eventCloseDate) ? fmtRange(new Date(eventOpenDate), new Date(eventCloseDate)) : ''}
                      readOnly
                      disabled={savingOrUploading}
                    />
                    {feedback('eventRange')}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">開催期間（表示用）</h4>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={eventPeriodDisplay}
                      onChange={(e) => setEventPeriodDisplay(e.target.value)}
                      disabled={savingOrUploading}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">関連リンク</h4>
                <div className="input-group">
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    disabled={savingOrUploading}
                  />
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '1' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('1')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '1'}
                  >
                    WebView
                  </button>
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '2' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('2')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '2'}
                  >
                    ブラウザ
                  </button>
                </div>
              </div>

              <div className="row">
                <div className={`col-md-6`}>
                  <div className="mb-4">
                    <h4 className="mb-3">関連チケット</h4>
                    {/* Spot select */}
                    <div className="input-group align-items-stretch">
                      <div className="flex-grow-1">
                        <Select
                          options={spotOptions}
                          value={spotOptions.find(o => o.value === spotId) || null}
                          onChange={(opt) => setSpotId(opt?.value || '')}
                          isDisabled={loadingSpots || savingOrUploading}
                          isClearable
                          placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                          classNamePrefix="react-select"
                          className="w-100"
                          menuPortalTarget={document.body}
                          styles={{
                            container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          filterOption={(opt, input) =>
                            opt.label.toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`col-md-6`}>
                  <h4 className="mb-3">&nbsp;</h4>
                  {/* Ticket select */}
                  <div className="input-group align-items-stretch mt-2">
                    <div className="flex-grow-1">
                      <Select
                        options={ticketOptions}
                        value={ticketOptions.find(o => o.value === ticketId) || null}
                        onChange={(opt) => setTicketId(opt?.value || '')}
                        isDisabled={!spotId || loadingTickets || savingOrUploading}
                        isClearable
                        placeholder={!spotId ? '' : (loadingTickets ? '読み込み中…' : '乗車券選択')}
                        classNamePrefix="react-select"
                        className="w-100"
                        menuPortalTarget={document.body}
                        styles={{
                          container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        filterOption={(opt, input) =>
                          opt.label.toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </div>
                    <span
                      className="input-group-text"
                      role="button"
                      tabIndex={0}
                      aria-label="追加"
                      title="追加"
                      onClick={handleAddPair}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddPair(); }}
                      style={{ cursor: (!spotId || !ticketId) ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: (!spotId || !ticketId) ? 0.6 : 1 }}
                    >
                      ＋
                    </span>
                  </div>
                </div>
                {/* Pairs table */}
                <table className="table fs-9 mb-0">
                  <thead>
                    <tr>
                      <th className="align-middle ps-3" style={{ width: 40 }}></th>
                      <th className="align-middle ps-3">スポット名</th>
                      <th className="align-middle ps-3">乗車券名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.length === 0 ? (
                      <tr>
                        <td className="ps-3 text-body-tertiary" colSpan={3}>追加されたデータはありません</td>
                      </tr>
                    ) : (
                      pairs.map((p, idx) => (
                        <tr key={`${p.spotId}-${p.ticketId}-${idx}`}>
                          <td className="ps-3">
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              title="削除"
                              aria-label="削除"
                              onClick={() => handleRemovePair(idx)}
                              style={{ fontSize: 18, lineHeight: 1, textDecoration: 'none' }}
                            >
                              ×
                            </button>
                          </td>
                          <td className="ps-3">{p.spotName}</td>
                          <td className="ps-3">{p.ticketName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="row mt-6">
                <div className={`col-md-6`}>
                  <div className="mb-4">
                    <h4 className="mb-3">関連クーポン</h4>
                    {/* Spot select */}
                    <div className="input-group align-items-stretch">
                      <div className="flex-grow-1">
                        <Select
                          options={spotOptions}
                          value={spotOptions.find(o => o.value === spotIdCoupon) || null}
                          onChange={(opt) => setSpotIdCoupon(opt?.value || '')}
                          isDisabled={loadingSpots || savingOrUploading}
                          isClearable
                          placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                          classNamePrefix="react-select"
                          className="w-100"
                          menuPortalTarget={document.body}
                          styles={{
                            container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          filterOption={(opt, input) =>
                            opt.label.toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <h4 className="mb-3">&nbsp;</h4>
                  {/* Ticket select */}
                  <div className="input-group align-items-stretch mt-2">
                    <div className="flex-grow-1">
                      <Select
                        options={couponOptions}
                        value={couponOptions.find(o => o.value === couponId) || null}
                        onChange={(opt) => setCouponId(opt?.value || '')}
                        isDisabled={!spotIdCoupon || loadingCoupons || savingOrUploading}
                        isClearable
                        placeholder={!spotIdCoupon ? '' : (loadingCoupons ? '読み込み中…' : 'クーポン選択')}
                        classNamePrefix="react-select"
                        className="w-100"
                        menuPortalTarget={document.body}
                        styles={{
                          container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        filterOption={(opt, input) =>
                          opt.label.toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </div>
                    <span
                      className="input-group-text"
                      role="button"
                      tabIndex={0}
                      aria-label="追加"
                      title="追加"
                      onClick={handleAddCouponPair}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddCouponPair(); }}
                      style={{ cursor: (!spotIdCoupon || !couponId) ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: (!spotIdCoupon || !couponId) ? 0.6 : 1 }}
                    >
                      ＋
                    </span>
                  </div>
                </div>
                {/* Pairs table */}
                <table className="table fs-9 mb-0">
                  <thead>
                    <tr>
                      <th className="align-middle ps-3" style={{ width: 40 }}></th>
                      <th className="align-middle ps-3">スポット名</th>
                      <th className="align-middle ps-3">クーポン名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponPairs.length === 0 ? (
                      <tr>
                        <td className="ps-3 text-body-tertiary" colSpan={3}>追加されたデータはありません</td>
                      </tr>
                    ) : (
                      couponPairs.map((p, idx) => (
                        <tr key={`${p.spotId}-${p.couponId}-${idx}`}>
                          <td className="ps-3">
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              title="削除"
                              aria-label="削除"
                              onClick={() => handleRemoveCouponPair(idx)}
                              style={{ fontSize: 18, lineHeight: 1, textDecoration: 'none' }}
                            >
                              ×
                            </button>
                          </td>
                          <td className="ps-3">{p.spotName}</td>
                          <td className="ps-3">{p.couponName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

            </div>

            <div className="col-12 col-xl-4">
              <div className="card mb-3" data-error-key="publishedFlag">
                <div className="card-body">
                  <div className={`mb-4 ${invalid('publishedFlag')}`}>
                    <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                    <div className="d-flex align-items-center gap-3">
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="publish_status"
                          value="0"
                          checked={publishedFlag === false}
                          onChange={() => setPublishedFlag(false)}
                          disabled={savingOrUploading}
                        /> 非公開
                      </label>
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="publish_status"
                          value="1"
                          checked={publishedFlag === true}
                          onChange={() => setPublishedFlag(true)}
                          disabled={savingOrUploading}
                        /> 公開
                      </label>
                    </div>
                    {feedback('publishedFlag')}
                  </div>

                  <div className="mb-4" data-error-key="publishedRange">
                    <div className="d-flex flex-wrap mb-2">
                      <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                    </div>
                    <div className="input-group">
                      <input
                        className={`form-control ${invalid('publishedRange')}`}
                        type="text"
                        id="public_range_side"
                        placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                        defaultValue={(publishedStart && publishedEnd) ? fmtRange(new Date(publishedStart), new Date(publishedEnd)) : ''}
                        readOnly
                        disabled={savingOrUploading}
                      />
                      <span
                        className="input-group-text"
                        role="button"
                        tabIndex={0}
                        onClick={clearPublish}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublish(); }}
                        aria-label="クリア"
                        title="クリア"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >×</span>
                    </div>
                    {feedback('publishedRange')}
                  </div>

                  <div className="mb-4" data-error-key="displayOrder">
                    <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                    <input
                      className={`form-control ${invalid('displayOrder')}`}
                      type="text"
                      value={displayOrder}
                      onChange={(e) => setDisplayOrder(e.target.value)}
                      disabled={savingOrUploading}
                    />
                    {feedback('displayOrder')}
                  </div>

                  <div className="mb-4">
                    <h5 className="mb-3">キャンペーン応募ボタン表示状態</h5>
                    <div className="d-flex align-items-center gap-3">
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="campaign_button"
                          value="0"
                          checked={!campaignButtonOn}
                          onChange={() => setCampaignButtonOn(false)}
                          disabled={savingOrUploading}
                        /> OFF
                      </label>
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="campaign_button"
                          value="1"
                          checked={campaignButtonOn}
                          onChange={() => setCampaignButtonOn(true)}
                          disabled={savingOrUploading}
                        /> ON
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-xl-12" data-error-key="campaignPoint">
                    <div className="mb-4">
                      <h4 className="mb-3">キャンペーンポイント<span className="text-danger">*</span></h4>
                      <input
                        className={`form-control ${invalid('campaignPoint')}`}
                        type="number"
                        name="campaign_point"
                        value={campaignPoint}
                        onChange={(e) => setCampaignPoint(e.target.value)}
                      />
                      {feedback('campaignPoint')}
                    </div>
                  </div>

                </div>
              </div>

              {/* Image card (single) */}
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title mb-3">キャンペーン画像</h4>

                  {!imagePreview && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handlePickClick}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragOver}
                      onDragLeave={handleDragLeave}
                      className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                    >
                      <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                      <p className="mb-2">または</p>
                      <button type="button" className="btn btn-outline-primary" disabled={savingOrUploading}>ファイルを選択</button>
                      <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    disabled={savingOrUploading}
                  />

                  {imagePreview && (
                    <div className="d-flex align-items-start gap-3">
                      <img
                        src={imagePreview}
                        alt="プレビュー"
                        className="rounded border"
                        style={{ width: 160, height: 160, objectFit: 'cover' }}
                      />
                      <div className="flex-grow-1">
                        <div className="mb-2 small text-body-secondary">
                          {imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : ''}
                        </div>
                        <div className="d-flex gap-2">
                          <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingOrUploading}>
                            画像を変更
                          </button>
                          <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingOrUploading}>
                            画像を削除
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>

            </div>
          </div>
        </form>
      </>
    </Layout >
  );
}
